import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import { useRequestPortfolioAdvice } from '../api/bots'
import { useToast } from '../components/Toast'

/** Dónde se muestra una lectura del asesor: el panel lateral (default) o la
 *  ventana centrada de siempre. */
export type AdvisorDisplayMode = 'panel' | 'modal'

type AdvisorState = {
  displayMode: AdvisorDisplayMode
  panelOpen: boolean
  modalOpen: boolean
  activeAlertId: number | null
}

const STORAGE_KEYS = {
  displayMode: 'deltabot:advisor-display',
  panelOpen: 'deltabot:advisor-panel-open',
  activeAlertId: 'deltabot:advisor-active-alert',
} as const

// Mismo criterio que lib/sidebarPin.ts: si localStorage no está disponible
// (modo privado, sitio bloqueado) se degrada al default en vez de romper.
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Sin persistencia el asesor sigue funcionando, sólo olvida el estado al recargar.
  }
}

function loadState(): AdvisorState {
  const storedId = Number(readStorage(STORAGE_KEYS.activeAlertId))
  return {
    displayMode: readStorage(STORAGE_KEYS.displayMode) === 'modal' ? 'modal' : 'panel',
    panelOpen: readStorage(STORAGE_KEYS.panelOpen) === 'true',
    modalOpen: false,
    activeAlertId: Number.isInteger(storedId) && storedId > 0 ? storedId : null,
  }
}

type AdvisorContextValue = AdvisorState & {
  /** Abre una lectura en el contenedor que corresponda al modo elegido. */
  openReading: (alertId: number) => void
  /** Botón ✨ de la barra: abre o cierra el panel (o la ventana, en modo ventana). */
  toggleAdvisor: () => void
  closePanel: () => void
  closeModal: () => void
  /** Cambia el modo y abre el asesor en ese contenedor, con la lectura activa. */
  setDisplayMode: (mode: AdvisorDisplayMode) => void
  requestReading: (presetId: number) => Promise<void>
  isRequesting: boolean
  /** Último pedido que falló, para ofrecer "Reintentar" en el panel o la ventana. */
  failedRequest: { presetId: number; message: string } | null
}

const AdvisorContext = createContext<AdvisorContextValue | null>(null)

export function AdvisorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdvisorState>(loadState)
  const requestAdvice = useRequestPortfolioAdvice()
  const toast = useToast()
  const [failedRequest, setFailedRequest] = useState<{ presetId: number; message: string } | null>(
    null,
  )

  const update = useCallback((patch: Partial<AdvisorState>) => {
    setState((current) => {
      const next = { ...current, ...patch }
      writeStorage(STORAGE_KEYS.displayMode, next.displayMode)
      writeStorage(STORAGE_KEYS.panelOpen, String(next.panelOpen))
      writeStorage(STORAGE_KEYS.activeAlertId, next.activeAlertId ? String(next.activeAlertId) : null)
      return next
    })
  }, [])

  const openReading = useCallback(
    (alertId: number) => {
      setState((current) => {
        const next = {
          ...current,
          activeAlertId: alertId,
          panelOpen: current.displayMode === 'panel',
          modalOpen: current.displayMode === 'modal',
        }
        writeStorage(STORAGE_KEYS.displayMode, next.displayMode)
        writeStorage(STORAGE_KEYS.panelOpen, String(next.panelOpen))
        writeStorage(STORAGE_KEYS.activeAlertId, String(alertId))
        return next
      })
    },
    [],
  )

  const toggleAdvisor = useCallback(() => {
    setState((current) => {
      const next =
        current.displayMode === 'panel'
          ? { ...current, panelOpen: !current.panelOpen }
          : { ...current, modalOpen: !current.modalOpen }
      writeStorage(STORAGE_KEYS.panelOpen, String(next.panelOpen))
      return next
    })
  }, [])

  const setDisplayMode = useCallback(
    (mode: AdvisorDisplayMode) => {
      setState((current) => {
        const next = {
          ...current,
          displayMode: mode,
          panelOpen: mode === 'panel',
          modalOpen: mode === 'modal',
        }
        writeStorage(STORAGE_KEYS.displayMode, mode)
        writeStorage(STORAGE_KEYS.panelOpen, String(next.panelOpen))
        return next
      })
    },
    [],
  )

  const closePanel = useCallback(() => update({ panelOpen: false }), [update])
  const closeModal = useCallback(() => update({ modalOpen: false }), [update])

  const requestReading = useCallback(
    async (presetId: number) => {
      setFailedRequest(null)
      try {
        const alert = await requestAdvice.mutateAsync(presetId)
        openReading(alert.id)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'No se pudo pedir la lectura de la flota'
        setFailedRequest({ presetId, message })
        toast.error(message)
      }
    },
    [requestAdvice, openReading, toast],
  )

  const value = useMemo<AdvisorContextValue>(
    () => ({
      ...state,
      openReading,
      toggleAdvisor,
      closePanel,
      closeModal,
      setDisplayMode,
      requestReading,
      isRequesting: requestAdvice.isPending,
      failedRequest,
    }),
    [
      state,
      openReading,
      toggleAdvisor,
      closePanel,
      closeModal,
      setDisplayMode,
      requestReading,
      requestAdvice.isPending,
      failedRequest,
    ],
  )

  return <AdvisorContext.Provider value={value}>{children}</AdvisorContext.Provider>
}

export function useAdvisor(): AdvisorContextValue {
  const value = useContext(AdvisorContext)
  if (!value) throw new Error('useAdvisor debe usarse dentro de AdvisorProvider')
  return value
}
